import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Statistic,
  Typography,
  Space,
  Spin,
  Alert,
} from "antd";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import { ExportOutlined } from "@ant-design/icons";
import moment from "moment";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "antd/dist/antd.css";
import useRequest from "../../hooks/useRequest";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Index = () => {
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState([moment().subtract(1, "month"), moment()]);
  const [filter, setFilter] = useState({ serviceType: "", region: "", vendor: "", customer: "", status: "" });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const { request } = useRequest();

  const fetchRevenueAnalytics = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `/analytics/revenue_analytics`,
        method: "GET",
      });
      if (response.status) {
        setData((prev) => ({
          ...prev,
          revenue_analytics: response.data.revenue_analytics || {},
        }));
      } else {
        setError("Failed to fetch revenue analytics");
      }
    } catch (err) {
      setError("Failed to fetch revenue analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceUsage = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `/analytics/service_usage_analytics`,
        method: "GET",
      });
      if (response.status) {
        setData((prev) => ({
          ...prev,
          service_usage: response.data.service_usage || {},
        }));
      } else {
        setError("Failed to fetch service usage analytics");
      }
    } catch (err) {
      setError("Failed to fetch service usage analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorPerformance = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `/analytics/vendor_performance_analytics`,
        method: "GET",
      });
      if (response.status) {
        setData((prev) => ({
          ...prev,
          vendor_performance: response.data.vendor_performance || {},
        }));
      } else {
        setError("Failed to fetch vendor performance analytics");
      }
    } catch (err) {
      setError("Failed to fetch vendor performance analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerInsights = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `/analytics/customer_insights_analytics`,
        method: "GET",
      });
      if (response.status) {
        setData((prev) => ({
          ...prev,
          customer_insights: response.data.customer_insights || {},
        }));
      } else {
        setError("Failed to fetch customer insights analytics");
      }
    } catch (err) {
      setError("Failed to fetch customer insights analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueAnalytics();
    fetchServiceUsage();
    fetchVendorPerformance();
    fetchCustomerInsights();
  }, [dateRange, filter]);

  // Prepare data for charts
  const kpiData = data.revenue_analytics || {};
  const revenueTrendData = (kpiData.region_breakdown || []).map((item) => ({
    month: item.location,
    revenue: item.total,
  }));
  const serviceUsageData = (data.service_usage?.most_requested || []).map((service) => ({
    service: service.name,
    usage: service.count,
    fulfillment: data.service_usage?.fulfillment_rate || 0,
  }));
  const vendorPerformanceData = (data.vendor_performance?.top_vendors || []).map((vendor) => ({
    vendor: vendor.name || "Unknown",
    score: vendor.score || (vendor.revenue ? 80 : 0), // Fallback score if not provided
    revenue: vendor.revenue || 0,
  }));
  const customerInsightsData = (data.customer_insights?.active_customers || []).map((customer) => ({
    region: customer.bookings__location || "Unknown",
    active: customer.count || 0,
    repeat: data.customer_insights?.repeat_usage || 0,
    churn: data.customer_insights?.churn_rate || 0,
  }));
  const customerDistributionData = [
    { name: "Repeat", value: data.customer_insights?.repeat_usage || 0 },
    {
      name: "New",
      value:
        (data.customer_insights?.active_customers?.reduce((sum, c) => sum + (c.count || 0), 0) || 0) -
        (data.customer_insights?.repeat_usage || 0),
    },
  ].filter((d) => d.value > 0); // Filter out zero values

  // Export functions
  const exportToCSV = () => {
    const headers = [
      { label: "Metric", key: "metric" },
      { label: "Value", key: "value" },
    ];
    const csvData = [
      { metric: "Total Revenue", value: `$${kpiData.total_revenue || 0}` },
      { metric: "Revenue Growth", value: `${kpiData.revenue_growth || 0}%` },
      { metric: "SLA Compliance", value: `${data.service_usage?.sla_compliance || 0}%` },
    ];
    return <CSVLink data={csvData} headers={headers} filename={`analytics-${moment().format("YYYY-MM-DD")}.csv`}>Download CSV</CSVLink>;
  };

  // Render loading or error states
  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" tip={`Fetching data... Attempt ${retryCount + 1}/3`} />
      </div>
    );
  }

  if (error && retryCount < 2) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error Fetching Data"
          description={
            <>
              {error}
              <br />
              <Button onClick={() => setRetryCount(retryCount + 1)} disabled={retryCount >= 2}>
                Retry
              </Button>
            </>
          }
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <Card title="Analytics Dashboard">
      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card style={{ textAlign: "center", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic
              title="Total Revenue"
              value={kpiData.total_revenue || 0}
              prefix="$"
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ textAlign: "center", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic
              title="Revenue Growth"
              value={kpiData.revenue_growth || 0}
              suffix="%"
              valueStyle={{ color: (kpiData.revenue_growth || 0) > 0 ? "#3f8600" : "#cf1322" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ textAlign: "center", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic title="Top Services" value={kpiData.top_services?.length || 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card style={{ textAlign: "center", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic title="SLA Compliance" value={data.service_usage?.sla_compliance || 0} suffix="%" />
          </Card>
        </Col>
      </Row>

      {/* Revenue Trend Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Revenue Trends" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {revenueTrendData.length > 0 ? (
              <LineChart
                width={800}
                height={300}
                data={revenueTrendData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#1890ff" strokeWidth={2} />
              </LineChart>
            ) : (
              <Alert message="No revenue trend data available" type="warning" showIcon />
            )}
          </Card>
        </Col>
      </Row>

      {/* Service Usage Bar Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Service Usage" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {serviceUsageData.length > 0 ? (
              <BarChart width={400} height={300} data={serviceUsageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="service" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="usage" fill="#52c41a" />
              </BarChart>
            ) : (
              <Alert message="No service usage data available" type="warning" showIcon />
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Fulfillment Rate" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic title="Fulfillment Rate" value={data.service_usage?.fulfillment_rate || 0} suffix="%" />
          </Card>
        </Col>
      </Row>

      {/* Vendor Performance */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Vendor Reliability Scores" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {vendorPerformanceData.length > 0 ? (
              <BarChart width={400} height={300} data={vendorPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="vendor" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" fill="#faad14" />
              </BarChart>
            ) : (
              <Alert message="No vendor performance data available" type="warning" showIcon />
            )}
          </Card>
        </Col>
        {/* <Col span={12}>
          <Card title="Vendor Count" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Statistic title="Onboarding" value={data.vendor_performance?.vendor_counts?.onboarding || 0} />
            <Statistic title="Active" value={data.vendor_performance?.vendor_counts?.active || 0} style={{ marginTop: 16 }} />
          </Card>
        </Col> */}
      </Row>

      {/* Customer Insights */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Customer Activity by Region" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {customerInsightsData.length > 0 ? (
              <BarChart width={400} height={300} data={customerInsightsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="region" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="active" fill="#722ed1" />
              </BarChart>
            ) : (
              <Alert message="No customer insights data available" type="warning" showIcon />
            )}
          </Card>
        </Col>
        {/* <Col span={12}>
          <Card title="Customer Distribution" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {customerDistributionData.length > 0 ? (
              <PieChart width={400} height={300}>
                <Pie
                  data={customerDistributionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  {customerDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={["#8884d8", "#82ca9d"][index % 2]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            ) : (
              <Alert message="No customer distribution data available" type="warning" showIcon />
            )}
          </Card>
        </Col> */}
      </Row>

      {/* Export Reports Section */}
      <Card title="Export Reports" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Form form={form} layout="vertical" onFinish={() => {}}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Form.Item label="Date Range">
                <RangePicker value={dateRange} onChange={setDateRange} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Service Type">
                <Select placeholder="All" onChange={(v) => setFilter({ ...filter, serviceType: v })}>
                  <Option value="Standard">Standard</Option>
                  <Option value="Premium">Premium</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Region">
                <Select placeholder="All" onChange={(v) => setFilter({ ...filter, region: v })}>
                  <Option value="North">North</Option>
                  <Option value="South">South</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Status">
                <Select placeholder="All" onChange={(v) => setFilter({ ...filter, status: v })}>
                  <Option value="Completed">Completed</Option>
                  <Option value="Pending">Pending</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]} justify="end">
            <Col>
              <div className="modal_footer">
                <Button
                  type="primary"
                  disabled={!data || !Object.values(data).some((v) => v && Object.keys(v).length)}
                >
                  {exportToCSV()}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>
    </Card>
  );
};

export default Index;